import React, { useCallback, useEffect, useState } from "react";
import { ActionButton, FormDialog, InputField, SelectField } from "../v3";
import { Box } from "@mui/material";
import { CompanyService } from "../../services";
import { toast } from "react-toastify";
import { formatPayload } from "../../utils";
import { companyValidationRules } from "../../pages/superadmin/validation";
import { GST_STATE_MAP } from "../../constants/gstStateMap";

const stateOptions = Object.entries(GST_STATE_MAP).map(([id, name]) => ({
  id,
  name,
}));

const getStateOptions = async ({ name = "" } = {}) => ({
  data: stateOptions.filter((option) => option.name.toLowerCase().includes(name.toLowerCase())),
});

export const UpdateCompany = ({ open, onClose, companyId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [companyDetails, setCompanyDetails] = useState({
    name: undefined,
    primaryPhone: undefined,
    primaryEmail: undefined,
    primaryAddress: {
      locality: undefined,
      state: undefined,
      stateCode: undefined,
      pinCode: undefined,
      country: undefined,
    },
    taxDetails: {
      gstNumber: undefined,
      panNumber: undefined,
    },
  });

  const fetchData = useCallback(async () => {
    try {
      const { data: company } = await CompanyService.getCompanyDetails(companyId);
      setCompanyDetails({
        name: company?.name,
        primaryPhone: company?.primaryPhone,
        primaryEmail: company?.primaryEmail,
        primaryAddress: {
          locality: company?.primaryAddress?.locality,
          state: company?.primaryAddress?.state,
          stateCode: company?.primaryAddress?.stateCode,
          pinCode: company?.primaryAddress?.pinCode,
          country: company?.primaryAddress?.country,
        },
        taxDetails: {
          gstNumber: company?.taxDetails?.gstNumber,
          panNumber: company?.taxDetails?.panNumber,
        },
      });
    } catch (error) {
      console.error("🚀 ~ fetchData ~ error:", error);
      toast.error("Failed to fetch company details");
    }
  }, [companyId]);

  useEffect(() => {
    if (open && companyId) fetchData();
  }, [fetchData, open, companyId]);

  const updateDetailsState = (event) => {
    const { name, value } = event.target;

    if (name === "gstNumber") {
      const gstNumber = String(value).toUpperCase();

      setCompanyDetails({
        ...companyDetails,
        taxDetails: {
          ...companyDetails.taxDetails,
          gstNumber,
        },
      });
    } else if (["locality", "state", "stateCode", "pinCode", "country"].includes(name)) {
      setCompanyDetails({
        ...companyDetails,
        primaryAddress: {
          ...companyDetails.primaryAddress,
          [name]: value,
        },
      });
    } else if (["panNumber"].includes(name)) {
      setCompanyDetails({
        ...companyDetails,
        taxDetails: {
          ...companyDetails.taxDetails,
          [name]: value,
        },
      });
    } else {
      setCompanyDetails({
        ...companyDetails,
        [name]: value,
      });
    }
  };
  const handleStateChange = (selectedState) => {
    setCompanyDetails((prev) => ({
      ...prev,
      primaryAddress: {
        ...prev.primaryAddress,
        state: selectedState?.name || "",
        stateCode: selectedState?.id || "",
      },
    }));
  };
  const handleClearForm = () => {
    setCompanyDetails({
      name: undefined,
      primaryPhone: undefined,
      primaryEmail: undefined,
      primaryAddress: {
        locality: undefined,
        state: undefined,
        stateCode: undefined,
        pinCode: undefined,
        country: undefined,
      },
    });
  };

  const handleCloseModal = () => {
    handleClearForm();
    setErrors({});
    onClose();
  };

  const formatCompanyPayload = (details) => {
    const payload = formatPayload({ ...details });

    if (payload.primaryAddress) {
      const address = payload.primaryAddress;
      const keys = ["locality", "state", "pinCode", "stateCode", "country"];

      keys.forEach((key) => {
        if (!address[key] || address[key].toString().trim() === "") {
          address[key] = "";
        }
      });
    }

    return payload;
  };

  const handleSaveCompany = async () => {
    setErrors({});
    const formattedData = formatCompanyPayload(companyDetails);
    const validationErrors = companyValidationRules(formattedData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setLoading(true);
      await CompanyService.updateCompany(companyId, formattedData);
      toast.success("Company details updated successfully");
      handleCloseModal();
      onSuccess?.();
    } catch (error) {
      toast.error("Error updating company details");
      console.error("🚀 ~ handleSaveCompany ~ error:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormDialog
      open={open}
      title="Update Company Details"
      actions={
        <>
          <ActionButton buttonText="Cancel" onButtonClick={handleCloseModal} variant="outlined" />
          <ActionButton buttonText="Save" onButtonClick={handleSaveCompany} disabled={loading} />
        </>
      }
    >
      <Box mt={1}>
        <Box display="flex" gap={2}>
          <InputField
            required
            label="Company Name"
            name="name"
            value={companyDetails.name}
            onChange={updateDetailsState}
            error={!!errors.name}
            helperText={errors.name}
            style={{ width: "100%" }}
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            required
            type="tel"
            label="Mobile"
            name="primaryPhone"
            value={companyDetails.primaryPhone}
            onChange={updateDetailsState}
            error={!!errors.primaryPhone}
            helperText={errors.primaryPhone}
            style={{ width: "100%" }}
          />
          <InputField
            required
            type="email"
            label="Email"
            name="primaryEmail"
            value={companyDetails.primaryEmail}
            onChange={updateDetailsState}
            error={!!errors.primaryEmail}
            helperText={errors.primaryEmail}
            style={{ width: "100%" }}
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            label="GST Number"
            name="gstNumber"
            value={companyDetails?.taxDetails?.gstNumber}
            onChange={updateDetailsState}
            error={!!errors.taxDetails?.gstNumber}
            helperText={errors.taxDetails?.gstNumber}
            style={{ width: "100%" }}
          />
          <InputField
            label="PAN Number"
            name="panNumber"
            value={companyDetails?.taxDetails?.panNumber}
            onChange={updateDetailsState}
            error={!!errors.taxDetails?.panNumber}
            helperText={errors.taxDetails?.panNumber}
            style={{ width: "100%" }}
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            label="Locality"
            name="locality"
            value={companyDetails.primaryAddress.locality}
            onChange={updateDetailsState}
            style={{ width: "100%" }}
          />

          <SelectField
            label="State"
            value={stateOptions.find((option) => option.id === companyDetails.primaryAddress.stateCode) || null}
            getData={getStateOptions}
            onChange={handleStateChange}
            style={{ width: "100%" }}
          />
        </Box>

        <Box display="flex" gap={2}>
          <InputField
            label="State Code"
            name="stateCode"
            value={companyDetails.primaryAddress.stateCode}
            disabled
            style={{ width: "100%" }}
          />

          <InputField
            label="Pin code"
            name="pinCode"
            type="number"
            value={companyDetails.primaryAddress.pinCode}
            onChange={updateDetailsState}
            error={!!errors?.primaryAddress?.pinCode}
            helperText={errors?.primaryAddress?.pinCode}
            style={{ width: "100%" }}
          />
        </Box>
        <Box display="flex" gap={2}>
          <InputField
            label="Country"
            name="country"
            value={companyDetails.primaryAddress.country}
            onChange={updateDetailsState}
            style={{ width: "100%" }}
          />
        </Box>
      </Box>
    </FormDialog>
  );
};
